import React, { useState, useMemo, useCallback } from "react";
import Model from "@tripian/model";
import {
  TripCard,
  ConfirmModalPopup,
  TabMenu,
  PreLoading,
} from "@tripian/react";
// import { useSelector } from 'react-redux';
import moment from "moment";
// import ICombinedState from '../../redux/model/ICombinedState';
import { api } from "@tripian/core";
import { useDispatch, useSelector } from "react-redux";
import { saveNotification } from "../../redux/action/trip";
import ICombinedState from "../../redux/model/ICombinedState";
import useTrip from "../../hooks/useTrip";
import ShareTripModal from "../ShareTripModal/ShareTripModal";
import useTranslate from "../../hooks/useTranslate";
import classes from "./TripList.module.scss";

interface ITripList {
  tripReferences: Model.TripReference[];
  loadingTripReferences: boolean;
  cardClicked: (tripHash: string) => void;
  cardEditClicked: (tripHash: string) => void;
  cardDeleteClicked: (tripHash: string) => Promise<boolean>;
  tripCreateClicked: () => void;
}

const TripList: React.FC<ITripList> = ({
  tripReferences,
  loadingTripReferences,
  cardClicked,
  cardEditClicked,
  cardDeleteClicked,
  tripCreateClicked,
}) => {
  const [modalState, setModalState] = useState({ show: false, tripHash: "" });
  const [isPastTrip, setIsPastTrip] = useState(false);
  const [icsLoading, setIcsLoading] = useState<{ tripHashes: string[] }>({
    tripHashes: [],
  });
  const [searchText, setSearchText] = useState("");

  const { t } = useTranslate();

  const { tripShare } = useTrip();
  const { shared } = useSelector((state: ICombinedState) => ({
    shared: state.trip.reference?.shared,
  }));

  const [shareModalState, setShareModalState] = useState<{
    show: boolean;
    loading: boolean;
    switchChecked: boolean;
    tripReference?: Model.TripReference;
  }>({
    show: false,
    loading: false,
    switchChecked: shared || false,
    tripReference: undefined,
  });

  const dispatch = useDispatch();

  const tripReferenceToShow = useMemo(() => {
    const now = moment(new Date()).format("X");
    const tripDate = (datetime: string) =>
      moment(datetime).utcOffset(0).format("X");

    return tripReferences
      .filter(trip => {
        const tripDepartureDate = tripDate(trip.tripProfile.departureDatetime);
        return isPastTrip ? tripDepartureDate < now : tripDepartureDate > now;
      })
      .filter(
        trip =>
          trip.tripProfile.tripName
            ?.toLowerCase()
            .includes(searchText.toLowerCase()) ||
          trip.city.name.toLowerCase().includes(searchText.toLowerCase())
      );
  }, [isPastTrip, tripReferences, searchText]);

  const modalFinally = useCallback(() => {
    setModalState({ show: false, tripHash: "" });
  }, []);

  const icsFileDownload = (tripHash: string) => {
    const newIcsLoading = { ...icsLoading };
    newIcsLoading.tripHashes.push(tripHash);
    setIcsLoading(newIcsLoading);
    api
      .tripDownloadIcs(tripHash)
      .catch(err => {
        dispatch(
          saveNotification(Model.NOTIFICATION_TYPE.ERROR, "planDownload", err)
        );
      })
      .finally(() => {
        const newIcsLoading = { ...icsLoading };
        const tripHashIndex = newIcsLoading.tripHashes.findIndex(
          t => t === tripHash
        );
        newIcsLoading.tripHashes.splice(tripHashIndex, 1);
        setIcsLoading(newIcsLoading);
      });
  };

  const switchCheckedOnchange = (checked: boolean, tripHash?: string) => {
    if (tripHash) {
      setShareModalState({ ...shareModalState, loading: true });
      tripShare(tripHash, checked).finally(() => {
        setShareModalState({
          ...shareModalState,
          switchChecked: checked,
          loading: false,
        });
      });
    }
  };

  return (
    <>
      <div className="col col12" style={{ padding: "0 10%" }}>
        <TabMenu
          menuItems={[
            {
              title: t("trips.myTrips.upComingTrips.title"),
              onClick: () => {
                setIsPastTrip(false);
              },
            },
            {
              title: t("trips.myTrips.pastTrips.title"),
              onClick: () => {
                setIsPastTrip(true);
              },
            },
          ]}
          selectedIndex={0}
        />
      </div>

      {loadingTripReferences ? (
        <PreLoading customClass="py8" />
      ) : (
        <>
          <ConfirmModalPopup
            openModalPopup={modalState.show}
            text={t("trips.deleteTrip.question")}
            confirmButtonText={t("trips.deleteTrip.submit")}
            cancelButtonText={t("trips.deleteTrip.cancel")}
            action={() => cardDeleteClicked(modalState.tripHash)}
            confirmationCallback={modalFinally}
            cancelCallback={modalFinally}
            className={classes.comfirmModal}
          />

          {shareModalState?.show && (
            <ShareTripModal
              onCancel={() =>
                setShareModalState({ ...shareModalState, show: false })
              }
              loading={shareModalState.loading}
              switchChecked={shareModalState.switchChecked}
              onChange={switchCheckedOnchange}
              cityName={shareModalState.tripReference?.city.name}
              arrivalDatetime={
                shareModalState.tripReference?.tripProfile.arrivalDatetime
              }
              departureDatetime={
                shareModalState.tripReference?.tripProfile.departureDatetime
              }
              tripHash={shareModalState.tripReference?.tripHash}
              dayIndex={0}
            />
          )}

          <div className="flex col col12 center mt-4 !mb-10">
            <div className="flex items-center border rounded-full px-4 py-2 w-full max-w-lg shadow-sm bg-[#f5f5f5] justify-self-center">
              <svg
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-6 h-6 text-[#61656E] mr-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 103.5 10.5a7.5 7.5 0 0013.15 6.15z"
                />
              </svg>
              <input
                type="text"
                placeholder={t("trips.searchPlaceHolder")}
                className="outline-none w-full text-base py-2 bg-[#f5f5f5]"
                value={searchText}
                onChange={e => setSearchText(e.target.value)}
              />
            </div>
          </div>

          {tripReferenceToShow.length === 0 ? (
            <>
              <div className="col col12 mt-12 text-2xl font-medium font-inter">
                {isPastTrip
                  ? t("trips.myTrips.pastTrips.emptyMessage")
                  : t("trips.myTrips.upComingTrips.emptyMessage")}
              </div>
            </>
          ) : (
            <div className="row gap-y-12">
              {tripReferenceToShow.map((tripReference, i) => (
                <div key={tripReference.id} className="col col12 col6-m">
                  <TripCard
                    tripReference={tripReference}
                    clicked={() => cardClicked(tripReference.tripHash)}
                    editTrip={() => cardEditClicked(tripReference.tripHash)}
                    deleteTrip={hash => {
                      setModalState({ show: true, tripHash: hash });
                    }}
                    icsFileDownload={icsFileDownload}
                    icsLoading={icsLoading.tripHashes.some(
                      tripHash => tripHash === tripReference.tripHash
                    )}
                    showShareTrip={window.tconfig.SHARED_TRIP}
                    shareTrip={tripReference => {
                      setShareModalState({
                        ...shareModalState,
                        show: true,
                        loading: false,
                        tripReference,
                        switchChecked: tripReference.shared,
                      });
                    }}
                    t={t}
                  />
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
};

export default TripList;
